import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Skull, Coins, Swords } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

/**
 * Wanted Posters — weekly bot bounty board.
 *
 * Pulls /api/bounties/weekly on mount. Each poster shows the bot's
 * name + tag, base_strength tier, and a moonstone reward. Pilots
 * who've beaten the flagged bot in a tournament this week can claim
 * the reward via /api/bounties/claim; others see a "Beat in
 * Tournaments" CTA that links to /tournaments.
 */
export default function Wanted() {
  const [board, setBoard] = useState(null);
  const [claiming, setClaiming] = useState(null);
  // Most Wanted Hunters leaderboard — top pilots by claimed bounties.
  // Toggleable between this week and all-time.
  const [hunters, setHunters] = useState([]);
  const [hunterWindow, setHunterWindow] = useState("week");

  const refresh = async () => {
    try {
      const r = await api.get("/bounties/weekly");
      setBoard(r.data);
    } catch {
      /* noop */
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    api
      .get(`/bounties/leaderboard?window=${hunterWindow}&limit=10`)
      .then((r) => setHunters(r.data?.hunters || []))
      .catch(() => setHunters([]));
  }, [hunterWindow]);

  const claim = async (botId) => {
    if (claiming) return;
    setClaiming(botId);
    try {
      const r = await api.post("/bounties/claim", { bot_id: botId });
      toast.success(`+${r.data.reward} ☾ Bounty collected!`, {
        description: `Wallet: ${r.data.new_balance} ☾`,
        icon: <Coins className="w-4 h-4 text-[#ffd700]" />,
      });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't claim bounty");
    } finally {
      setClaiming(null);
    }
  };

  const bounties = board?.bounties || [];

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-16 fade-in"
        data-testid="wanted-page"
      >
        <div className="mb-8">
          <div className="font-mono-cosmic text-[10px] tracking-[0.4em] text-[#ff5555]/80 uppercase mb-2">
            // BOUNTY BOARD · WEEK {board?.week_key || "—"}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-white">
            Wanted: Dead or Disconnected
          </h1>
          <p className="text-sm text-white/55 mt-2 max-w-2xl">
            Four AI pilots are flagged this week. Beat them in a
            tournament match this week to cash in the bounty —
            moonstones added straight to your wallet. Rotation refreshes
            every Monday 00:00 UTC.
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          data-testid="wanted-grid"
        >
          {bounties.length === 0 && (
            <div className="col-span-full border border-white/10 bg-black/30 p-8 text-center text-white/45 font-mono-cosmic text-xs tracking-[0.25em] uppercase">
              Loading bounty board…
            </div>
          )}
          {bounties.map((b) => {
            const tierLabel =
              b.base_strength >= 0.9
                ? "APEX"
                : b.base_strength >= 0.78
                  ? "ELITE"
                  : b.base_strength >= 0.65
                    ? "VETERAN"
                    : "ROOKIE";
            const tierColor =
              tierLabel === "APEX"
                ? "#b026ff"
                : tierLabel === "ELITE"
                  ? "#67e8f9"
                  : tierLabel === "VETERAN"
                    ? "#ffd700"
                    : "#cbd5e1";
            return (
              <div
                key={b.bot_id}
                className={`relative border ${
                  b.claimed
                    ? "border-[#28e070]/40 bg-[#28e070]/5"
                    : "border-[#ff5555]/40 bg-black/40 hover:border-[#ff5555]"
                } p-4 transition`}
                data-testid={`wanted-card-${b.bot_id}`}
                style={{
                  boxShadow: b.claimed
                    ? "0 0 24px rgba(40,224,112,0.15)"
                    : "0 0 24px rgba(255,85,85,0.18)",
                }}
              >
                {/* Old-west wanted-poster vibe — sharp top + skull */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/45">
                    // WANTED
                  </div>
                  <Skull
                    className={`w-4 h-4 ${
                      b.claimed ? "text-[#28e070]" : "text-[#ff5555]"
                    }`}
                  />
                </div>

                <Link
                  to={`/bots/${encodeURIComponent(b.bot_id)}`}
                  className="block hover:opacity-90 transition"
                >
                  <div className="font-heading text-xl text-white truncate">
                    {b.name}
                  </div>
                  <div className="font-mono-cosmic text-[10px] tabular-nums text-white/45">
                    #{b.tag}
                  </div>
                </Link>

                <div className="mt-3 flex items-center gap-2">
                  <div
                    className="px-1.5 py-0.5 border font-mono-cosmic text-[9px] tracking-[0.2em] uppercase"
                    style={{
                      borderColor: `${tierColor}66`,
                      color: tierColor,
                    }}
                  >
                    {tierLabel}
                  </div>
                  <div className="flex items-center gap-1 text-[#ffd700] font-mono-cosmic text-xs tabular-nums">
                    <Coins className="w-3 h-3" /> {b.reward} ☾
                  </div>
                </div>

                <div className="mt-4">
                  {b.claimed ? (
                    <div
                      className="w-full py-2 border border-[#28e070]/40 bg-[#28e070]/10 text-[#28e070] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-center"
                      data-testid={`wanted-claimed-${b.bot_id}`}
                    >
                      Bounty Collected ✓
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => claim(b.bot_id)}
                      disabled={claiming === b.bot_id}
                      className="w-full py-2 border border-[#ff5555] text-[#ff5555] hover:bg-[#ff5555]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase disabled:opacity-50"
                      data-testid={`wanted-claim-${b.bot_id}`}
                    >
                      {claiming === b.bot_id ? "Claiming…" : "Claim Bounty"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Most Wanted Hunters — top pilots by bounties claimed.
            Toggleable between this week and all-time so the meta has
            both fresh and career-grade competition. Renders even when
            empty so newcomers see the scaffolding. */}
        <section
          className="mt-10 border border-[#ffd700]/30 bg-[#ffd700]/5 p-4"
          data-testid="wanted-leaderboard"
        >
          <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700]/80 uppercase">
                // MOST WANTED HUNTERS
              </div>
              <h2 className="font-heading text-xl text-white mt-0.5">
                Top bounty hunters
              </h2>
            </div>
            <div className="flex items-center gap-1.5">
              {[
                { id: "week", label: "THIS WEEK" },
                { id: "all", label: "ALL TIME" },
              ].map((w) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setHunterWindow(w.id)}
                  className={`px-2 py-1 border font-mono-cosmic text-[9px] tracking-[0.25em] uppercase transition ${
                    hunterWindow === w.id
                      ? "border-[#ffd700] text-[#ffd700] bg-[#ffd700]/10"
                      : "border-white/15 text-white/55 hover:text-white"
                  }`}
                  data-testid={`wanted-leaderboard-window-${w.id}`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          {hunters.length === 0 ? (
            <div className="border border-white/10 bg-black/30 p-6 text-center text-white/40 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase">
              No bounties claimed{hunterWindow === "week" ? " this week" : ""} yet — first kill takes the crown.
            </div>
          ) : (
            <ul className="divide-y divide-white/10 border border-white/10 bg-black/30">
              {hunters.map((h, i) => (
                <li
                  key={`${h.tag}-${i}`}
                  className="px-3 py-2 flex items-center gap-3"
                  data-testid={`wanted-hunter-${i}`}
                >
                  <div
                    className={`font-mono-cosmic text-xs tabular-nums w-6 text-right ${
                      i === 0
                        ? "text-[#ffd700]"
                        : i === 1
                          ? "text-white/85"
                          : i === 2
                            ? "text-[#cd7f32]"
                            : "text-white/40"
                    }`}
                  >
                    #{i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-cosmic text-[11px] tracking-[0.15em] uppercase text-white/85 truncate">
                      {h.name}
                    </div>
                    <div className="font-mono-cosmic text-[9px] tabular-nums text-white/40">
                      #{h.tag}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono-cosmic text-[10px] tabular-nums text-white/55">
                      {h.kills} kill{h.kills === 1 ? "" : "s"}
                    </span>
                    <span className="text-white/20">·</span>
                    <span className="font-mono-cosmic text-[10px] tabular-nums text-[#ffd700] inline-flex items-center gap-1">
                      <Coins className="w-3 h-3" />
                      {h.winnings}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-4 gap-3 flex-wrap">
          <div className="text-[10px] font-mono-cosmic tracking-[0.25em] uppercase text-white/40">
            Beat a flagged bot in a tournament this week to claim its bounty.
          </div>
          <Link
            to="/tournaments"
            className="flex items-center gap-2 px-3 py-1.5 border border-[#ffd700] text-[#ffd700] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#ffd700]/10"
            data-testid="wanted-go-to-tournaments"
          >
            <Swords className="w-3 h-3" /> Open Tournaments
          </Link>
        </div>
      </main>
    </div>
  );
}
